import Feature from 'ol/Feature';
import { Point } from 'ol/geom';
import { fromLonLat } from 'ol/proj';
import { PositionAgeStatus, UpsertVesselFeatureOptions, VoyageLayersNames } from '../maplayers/types/voyage-layers.types';
import { VesselPositionDataExtended } from '../services/types/position-service.types';
import { smartAge } from '../utils/helpers';
import { UiStateModel } from './ui-state-model';
import { AlertId, VesselAlertsModel } from './vessel-alerts-model';
import { VesselsTrackingModel } from './vessels-tracking-model';
import { VoyFleetAlarmModel } from './voy-fleet-alarm-model';
import { getVoyageVesselCategory } from '../utils/fleet-point-icon-utils';

const _PROJECTION = (<any>window)["appenv"].projection;
const _POSITION_OUTDATED = 8 * 60 * 60 * 1000; // Eight hours in milliseconds
const _POSITION_OBSOLETE = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
const _DEFAULT_VESSEL_LENGTH = 280.0;
const _DEFAULT_VESSEL_WIDTH = 40.0;
const _MAX_VESSEL_LENGTH = 450.0;


export const FleetFeaturesUtilities = new class {
    public setAllFeatures(currentFeatures: Map<number, Feature<Point>>) {
        const positions = VesselsTrackingModel?.allVesselPositions;
        
        let currentSelectedVessel = Number(UiStateModel!.getValue("imo"));
        const addMaerskVessels: Feature<Point>[] = [];
        const addNonMearskVessels: Feature<Point>[] = [];
        
        positions?.forEach(pos => {
            this._setVesselFeature(pos,currentFeatures, currentSelectedVessel, addMaerskVessels, addNonMearskVessels)
        })
    }

    getFilteredFeatures(currentFeatures: Map<number, Feature<Point>>): Feature<Point>[] {
        const filteredPositions = Array.from(VesselsTrackingModel!.filteredVesselPositions.values());

        const filteredFeatures: Feature<Point>[] = [];
        const filteredNonMaerskFeatures: Feature<Point>[] = [];

        filteredPositions.forEach(fp => {
            if (currentFeatures.has(fp.imo)) {
                const feature = currentFeatures.get(fp.imo)!
                filteredFeatures.push(feature);
            }
        })

        return filteredFeatures;
    }

    private _setVesselFeature(vessel: VesselPositionDataExtended, currentFeatures: Map<number, Feature<Point>>, currentSelectedVessel: number, addMaerskVessels: Feature<Point>[], addNonMearskVessels: Feature<Point>[]) {
        if (!vessel.imo) {
            console.warn("A Vessel is missing IMO Number")
            return
        }

        const highlightVessel = (vessel && vessel.imo !== undefined && currentSelectedVessel === vessel.imo);

        if (vessel.longitude && vessel.latitude) {
            // only plot a vessel if we know the position

            // If the position is outdated, add an indicator
            const positionAge = (new Date().getTime() - new Date(vessel.timestamp).getTime());
            let outdatedPositionText = '';
            if (positionAge > _POSITION_OUTDATED) {
                const positionAgeText = smartAge(positionAge);

                outdatedPositionText = ` (position ${positionAgeText} ago)`;

            }

            const alarms = VoyFleetAlarmModel!.getVesselAlarmsBasicInfo(vessel.imo);
            const vesselCategory = getVoyageVesselCategory(vessel.imo);
            const heading = vessel.heading !== null ? vessel.heading : vessel.course !== null ? vessel.course : 0;

            const options: UpsertVesselFeatureOptions = {
                imoNo: vessel.imo,
                vesselName: vessel.vesselName,
                longitude: vessel.longitude,
                latitude: vessel.latitude,
                heading,
                vesselLength: vessel.vesselLength,
                vesselWidth: vessel.vesselWidth,
                highlight: highlightVessel,
                positionAge,
                outdatedPositionText,
                timestamp: vessel.timestamp,
                speed: vessel.sog,
                source: vessel.source,
                isMaersk: vessel.isMaersk,
                alarms,
                readingType: vessel.readingType,
                navStatus: vessel.navStatus,
                vesselCategory,
                vesselType: vessel.vesselType,
                isSelected: false
            }

            const feature = this._upsertVesselFeature(options);
            if (feature) {
                if (vessel.isMaersk) {
                    addMaerskVessels.push(feature);
                } else {
                    addNonMearskVessels.push(feature);
                }
            }

            currentFeatures.set(vessel.imo, feature)
        }
    }

    private _upsertVesselFeature(options: UpsertVesselFeatureOptions): Feature<Point> {
        const { vesselName, longitude, latitude, imoNo, speed, heading, timestamp, source, alarms, highlight = false, positionAge, vesselLength, vesselWidth, isMaersk, readingType, navStatus, vesselCategory, isSelected, vesselType } = options;
        const coords = fromLonLat([longitude, latitude], _PROJECTION);

        const shipPoint = new Point(coords);
        const vesselData = {
            name: vesselName,
            speed: speed,
            heading,
            timestamp,
            source,
            imoNo,
            alarms,
            readingType,
            navStatus
        };

        // vessel doesn't already exist, so create a feature for it
        const newFeature = new Feature({
            geometry: shipPoint,
            imoNo,
            highlight,
            length: vesselLength || _DEFAULT_VESSEL_LENGTH,
            width: vesselWidth || _DEFAULT_VESSEL_WIDTH,
            heading,
            positionAge: this._getPositionAgeStatus(positionAge),
            layer: VoyageLayersNames.VesselsLayer,
            isMaersk,
            vesselInfo: vesselData,
            hasAlarms: alarms.length ? true : false,
            vesselCategory,
            vesselType,
            isSelected
        });
        newFeature.setId(imoNo);

        return newFeature;
    }

    private _getPositionAgeStatus(positionAge: number): PositionAgeStatus {
        if (positionAge < _POSITION_OUTDATED) {
            return PositionAgeStatus.NORMAL;
        }
        if (positionAge < _POSITION_OBSOLETE) {
            return PositionAgeStatus.OUTDATED;
        }
        return PositionAgeStatus.OBSOLETE;
    }
}
